import { DateTime } from 'luxon'
import { z } from 'zod'
import { EventType } from './types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const yyyyMMddThhmmssRegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/

const zodTimestampAsDateTime = z.preprocess(
	// Appends a `Z` at the end of `0000-00-00T00:00:00` timestamp so that zod's `datetime` parser can handle it correctly
	(timestamp) => (typeof timestamp === 'string' && timestamp.trim().match(yyyyMMddThhmmssRegExp) ? `${timestamp}Z` : timestamp),
	z
		.string()
		.datetime({ offset: true })
		.transform((timestamp) => DateTime.fromISO(timestamp, { zone: 'UTC' }))
)

export const zodDateStringAsDateTime = z
	.string()
	.date()
	.transform((timestamp) => DateTime.fromISO(timestamp, { zone: 'UTC' }))

const storeNames = [
	'steam',
	'nintendo_switch_americas',
	'nintendo_switch_europe_australia',
	'nintendo_switch_japan',
	'nintendo_switch_china',
	'nintendo_switch_korea',
	'nintendo_switch_taiwan_hong_kong',
	'nintendo_switch_southeast_asia',
	'nintendo_switch_unknown',
	'nintendo_switch_2_americas',
	'nintendo_switch_2_europe_australia',
	'nintendo_switch_2_japan',
	'nintendo_switch_2_china',
	'nintendo_switch_2_korea',
	'nintendo_switch_2_taiwan_hong_kong',
	'nintendo_switch_2_southeast_asia',
	'nintendo_switch_2_unknown',
	'nintendo_other_americas',
	'nintendo_other_europe_australia',
	'nintendo_other_japan',
	'nintendo_other_china',
	'nintendo_other_korea',
	'nintendo_other_taiwan_hong_kong',
	'nintendo_other_southeast_asia',
	'nintendo_other_unknown',
	'playstation_asia',
	'playstation_japan',
	'playstation_europe',
	'playstation_america',
	'playstation_unknown',
	'microsoft',
	'meta_rift',
	'meta_quest',
	'epic',
	'gog',
	'humble',
	'apple',
	'google'
] as const

const skuIdSchema = z.string()
const storeNameSchema = z.enum(storeNames)

const isValidStoreName = (name: unknown): name is (typeof storeNames)[number] => {
	return typeof name === 'string' && storeNames.includes(name as (typeof storeNames)[number])
}

const createLenientStoreNameSchema = <T extends z.ZodTypeAny>(baseSchema: T, getStoreName: (entry: unknown) => unknown, errorContext: string) => {
	return z.preprocess((data) => {
		if (!Array.isArray(data)) return data

		const unrecognizedStoreNames: string[] = []
		const invalidEntries: unknown[] = []

		const filteredArray = data.filter((entry) => {
			if (typeof entry === 'object' && entry !== null) {
				const storeName = getStoreName(entry)

				if (isValidStoreName(storeName)) {
					return true
				}

				if (typeof storeName === 'string') {
					unrecognizedStoreNames.push(storeName)
				}

				return false
			}

			invalidEntries.push(entry)

			return false
		})

		if (unrecognizedStoreNames.length > 0) {
			const { trackException } = useTelemetry()

			trackException(new Error(errorContext), errorContext, {
				unrecognizedStoreNames: Array.from(new Set(unrecognizedStoreNames)),
				invalidEntries: invalidEntries.map((entry) => JSON.stringify(entry))
			})
		}

		return filteredArray
	}, baseSchema)
}

const customerCreatedEventUuidSchema = z.string()

const discountEventTypeSchema = z.enum(['discount', 'store_discount'])

const eventOriginSchema = z.enum(['scraped', 'detected', 'customer_created', 'calculated'])

const opportunityTypeSchema = z.enum(['calculated', 'store'])

const cooldownDirectionSchema = z.enum(['forward', 'backward'])

const opportunityOriginSchema = z.object({
	name: z.string(),
	opportunity_type: opportunityTypeSchema,
	start_date: zodTimestampAsDateTime,
	end_date: zodTimestampAsDateTime,
	discount_depth: z.number(),
	create_time: zodTimestampAsDateTime.optional(),
	update_time: zodTimestampAsDateTime.optional(),
	triggers_cooldown: z.boolean().optional(),
	major: z.boolean().optional(),
	discount_event_type: discountEventTypeSchema.optional()
})

export type OpportunityOrigin = z.infer<typeof opportunityOriginSchema>

export const noteSchema = z.object({
	id: z.string(),
	text: z.string(),
	create_time: zodTimestampAsDateTime,
	update_time: zodTimestampAsDateTime.optional()
})

export const eventDataSchema = z.object({
	hash: z.string(),
	id: z.string().or(z.null()).optional(),
	unique_sku_id: skuIdSchema,
	store_name: storeNameSchema,
	name: z.string().optional(),
	discount_depth: z.number().optional(),
	event_type: z.nativeEnum(EventType),
	start_date: zodTimestampAsDateTime,
	end_date: zodTimestampAsDateTime,
	recommended_discount_depth: z.number().optional(),
	recommended_start_date: zodTimestampAsDateTime.optional(),
	recommended_end_date: zodTimestampAsDateTime.optional(),
	isRecommendationAvailable: z.boolean().optional(),
	opportunity_type: opportunityTypeSchema.optional(),
	cooldown_direction: cooldownDirectionSchema.optional(),
	event_origin: eventOriginSchema.optional(),
	discount_event_type: discountEventTypeSchema.optional(),
	triggers_cooldown: z.boolean().optional(),
	major: z.boolean().optional(),
	create_time: zodTimestampAsDateTime.optional(),
	update_time: zodTimestampAsDateTime.optional(),
	opportunity_origin: opportunityOriginSchema.optional(),
	notes: z.array(noteSchema).optional(),
	source_event_hash: z.string().optional(),
	is_curated_discount: z.boolean().optional()
})

export const productSchema = z.object({
	unique_sku_id: skuIdSchema,
	human_name: z.string(),
	base_sku_id: z.string(),
	product_name: z.string(),
	is_minor: z.boolean(),
	store_specific_sku_id: z.string().nullable()
})

export const skuDataSchema = productSchema.extend({
	events: z.array(eventDataSchema)
})

export const storeDataSchema = z.object({
	studio_id: z.number(),
	store_name: storeNameSchema,
	start_date: zodDateStringAsDateTime,
	end_date: zodDateStringAsDateTime,
	skus: z.array(skuDataSchema)
})

export const historyEventSchema = z.object({
	unique_sku_id: z.string(),
	portal: z.string(),
	event_name: z.string(),
	datetime_from: zodTimestampAsDateTime,
	datetime_to: zodTimestampAsDateTime,
	discount_depth: z.number(),
	units: z.number(),
	major: z.boolean(),
	revenue: z.number(),
	baseline_units: z.number(),
	baseline_revenue: z.number(),
	uplift_units: z.number(),
	uplift_revenue: z.number(),
	uplift_score: z.number()
})

export const historyEventsListSchema = z.array(historyEventSchema)

export const recommendationSchema = z.object({
	depth: z.number().optional(),
	start_date: zodTimestampAsDateTime.optional(),
	end_date: zodTimestampAsDateTime.optional(),
	is_recommendation_available: z.boolean()
})

export const recommendationsSchema = z.record(customerCreatedEventUuidSchema, recommendationSchema).optional()

export const recommendedDatesSchema = z.object({
	start_date: zodTimestampAsDateTime.optional(),
	end_date: zodTimestampAsDateTime.optional()
})

export const recommendedDatesMapSchema = z.record(skuIdSchema, recommendedDatesSchema)

export const saleDaysSummaryForSKUSchema = z.object({
	uniqueSkuId: skuIdSchema,
	humanName: z.string(),
	score: z.number(),
	availableSaleDays: z.number(),
	actualSaleDays: z.number(),
	missedSaleDays: z.number(),
	recommendedTimeline: z.array(eventDataSchema).optional(),
	actualTimeline: z.array(eventDataSchema)
})

export const saleDaysSummaryForProductSchema = z.object({
	name: z.string(),
	score: z.number(),
	skus: z.array(saleDaysSummaryForSKUSchema)
})

export const saleDaysSummaryForStoreSchema = z.object({
	storeName: storeNameSchema,
	score: z.number(),
	recommendedTimeline: z.array(eventDataSchema).optional(),
	products: z.array(saleDaysSummaryForProductSchema)
})

export const saleDaysSummarySchema = z.object({
	startDate: zodTimestampAsDateTime,
	endDate: zodTimestampAsDateTime,
	score: z.number(),
	stores: z.array(saleDaysSummaryForStoreSchema)
})

export const actionsSchema = z.object({
	unplannedEvents: z.array(eventDataSchema),
	unscheduledEvents: z.array(eventDataSchema)
})

export const storeSchema = z.object({
	id: storeNameSchema,
	displayName: z.string(),
	timeZone: z.string().optional()
})

export const storesSchemaLenient = createLenientStoreNameSchema(
	z.array(storeSchema),
	(entry) => (entry as { id?: unknown }).id,
	'Invalid store name in stores'
)

const observationTypes = [
	'discounts',
	'sales',
	'wishlist_actions',
	'visibility',
	'wishlist_cohorts',
	'cumulative_wishlist_sales',
	'wishlist_balance',
	'daily_active_users'
] as const

const lastScrapeDateSchema = z.preprocess(
	(data) => {
		if (
			data &&
			typeof data === 'object' &&
			'observationType' in data &&
			typeof data.observationType === 'string' &&
			!observationTypes.includes(data.observationType as (typeof observationTypes)[number])
		) {
			const message = 'Unknown observation type in last scrape date'

			useTelemetry().trackException(message, message, {
				data: data.observationType
			})
		}

		return data
	},
	z.object({
		portal: z.string(),
		storeName: storeNameSchema,
		date: zodTimestampAsDateTime,
		observationType: z.string()
	})
)

export const lastScrapeDatesSchemaLenient = createLenientStoreNameSchema(
	z.array(lastScrapeDateSchema),
	(entry) => (entry as { storeName?: unknown }).storeName,
	'Invalid store name in last scrape dates'
)

export const discountTimeRuleSchema = z.object({
	hour: z.object({
		default: z.number(),
		fixed: z.boolean().optional()
	}),
	minute: z.object({
		default: z.number(),
		fixed: z.boolean().optional()
	}),
	second: z.object({
		default: z.number(),
		fixed: z.boolean().optional()
	})
})

export const discountConfigSchema = z.object({
	cooldownBeforeLengthInDays: z.number().optional(),
	cooldownAfterLengthInDays: z.number().optional(),
	minDepth: z.number(),
	maxDepth: z.number(),
	minLengthInDays: z.number(),
	optimalLengthInDays: z.number(),
	maxLengthInDays: z.number(),
	leadTimeInDays: z.number(),
	startTimeRules: discountTimeRuleSchema,
	endTimeRules: discountTimeRuleSchema,
	ianaTimeZoneName: z.string()
})
